import { Metric } from '@strength/domain';
import {
  MetricBenchmarkData,
  formatExercisePayload,
  getShorthandMetrics,
  shouldSkipEntry,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSection,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { sortByOrder } from '@strength/utils';

export type ExerciseDisplayLine = {
  key: string;
  name: string;
  prescriptionLines: string[];
};

export type ExerciseDisplayGroup = ExerciseDisplayLine & {
  isSuperset: boolean;
};

// Renders a workout exercise's prescription as one or more shorthand lines
// (e.g. "3 x 8 @ 200lbs, Tempo 1-2-2, RPE 8"), matching the format used by
// Session/Cycle views and the tablet actuals view. Draft (sets-as-metric)
// rows are pre-expanded into the published shape that getShorthandMetrics
// expects.
export function getPrescriptionLines(
  exercise: WorkoutExercise,
  metrics: Metric[],
  t: I18nTranslator,
  applyBenchmarkData: MetricBenchmarkData,
): string[] {
  if (!exercise.sets.length) return [];
  const { exerciseMetrics, sets } = expandDraftSetsForDisplay(
    exercise.metrics,
    exercise.sets,
  );
  if (!sets.length) return [];
  const payload = formatExercisePayload(exercise, '', undefined);
  return getShorthandMetrics(
    payload,
    exercise.libraryExerciseId,
    applyBenchmarkData,
    exerciseMetrics,
    sets,
    metrics,
    t,
  );
}

// Draft prescriptions store the set count as a `sets`-type metric on each row
// (the row's multiplier), unlike published layers where each set is already
// its own row. `applyCondensedMetrics` expects the published shape, so we
// pre-expand each draft row into N synthetic sets and drop the sets metric.
export function expandDraftSetsForDisplay(
  exerciseMetrics: WorkoutExercise['metrics'],
  sets: WorkoutExercise['sets'],
): {
  exerciseMetrics: WorkoutExercise['metrics'];
  sets: WorkoutExercise['sets'];
} {
  const setsMetric = exerciseMetrics.find((m) => m.metricType === 'sets');
  if (!setsMetric) return { exerciseMetrics, sets };

  const filteredMetrics = exerciseMetrics.filter(
    (m) => m.metricType !== 'sets',
  );

  const expandedSets: WorkoutExercise['sets'] = [];
  let sortOrder = 0;
  for (const set of sets) {
    const setsValue = set.metrics.find(
      (m) => m.metricId === setsMetric.metricId,
    )?.input;
    const parsed = parseInt(setsValue ?? '', 10);
    const count = Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    for (let i = 0; i < count; i++) {
      expandedSets.push({ ...set, sortOrder: sortOrder++ });
    }
  }

  return { exerciseMetrics: filteredMetrics, sets: expandedSets };
}

export function exerciseToDisplayLine(
  exercise: WorkoutExercise,
  key: string,
  metrics: Metric[],
  t: I18nTranslator,
  applyBenchmarkData: MetricBenchmarkData,
): ExerciseDisplayLine {
  return {
    key,
    name: exercise.exerciseName,
    prescriptionLines: getPrescriptionLines(
      exercise,
      metrics,
      t,
      applyBenchmarkData,
    ),
  };
}

// For an alternate group, the base is the currently selected exercise; if
// selectedExerciseId doesn't resolve, fall back to sortOrder 0, then first.
// The remaining alternates aren't rendered on the calendar — only the base is.
export function altGroupToDisplayGroup(
  altGroup: WorkoutAlternateGroup,
  isSuperset: boolean,
  metrics: Metric[],
  t: I18nTranslator,
  applyBenchmarkData: MetricBenchmarkData,
): ExerciseDisplayGroup | null {
  const sorted = sortByOrder(altGroup.exercises);
  const base =
    sorted.find((a) => a.workoutExerciseId === altGroup.selectedExerciseId) ??
    sorted.find((a) => a.sortOrder === 0) ??
    sorted[0];
  if (!base) return null;
  return {
    ...exerciseToDisplayLine(
      base.exercise,
      base.workoutAlternateExerciseId,
      metrics,
      t,
      applyBenchmarkData,
    ),
    isSuperset,
  };
}

// Builds the per-exercise display groups for a section, reconciling the
// overlap between supersets and alternate groups: an exercise that lives in
// both still produces a single line (with the superset indicator and its
// alternates attached). Without this dedupe step the calendar card renders
// the same exercise twice — once from the superset entry, once from the
// alternate group entry — and the alternate exercises never appear.
export function getSectionExerciseGroups(
  section: WorkoutSection,
  metrics: Metric[],
  t: I18nTranslator,
  applyBenchmarkData: MetricBenchmarkData,
): ExerciseDisplayGroup[] {
  const altGroupByExerciseId = new Map<string, WorkoutAlternateGroup>();
  for (const entry of section.entries) {
    if (entry.__type === 'WorkoutAlternateGroup') {
      for (const ae of entry.exercises) {
        altGroupByExerciseId.set(ae.workoutExerciseId, entry);
      }
    }
  }

  const groups: ExerciseDisplayGroup[] = [];

  for (const entry of section.entries) {
    if (shouldSkipEntry(entry, section.entries)) continue;
    if (entry.__type === 'WorkoutExercise') {
      groups.push({
        ...exerciseToDisplayLine(
          entry,
          entry.workoutExerciseId,
          metrics,
          t,
          applyBenchmarkData,
        ),
        isSuperset: false,
      });
    } else if (entry.__type === 'WorkoutSuperset') {
      for (const supersetExercise of sortByOrder(entry.exercises)) {
        const altGroup = altGroupByExerciseId.get(
          supersetExercise.exercise.workoutExerciseId,
        );
        if (altGroup) {
          const group = altGroupToDisplayGroup(
            altGroup,
            true,
            metrics,
            t,
            applyBenchmarkData,
          );
          if (group) {
            groups.push({
              ...group,
              key: supersetExercise.workoutSupersetExerciseId,
            });
          }
        } else {
          groups.push({
            ...exerciseToDisplayLine(
              supersetExercise.exercise,
              supersetExercise.workoutSupersetExerciseId,
              metrics,
              t,
              applyBenchmarkData,
            ),
            isSuperset: true,
          });
        }
      }
    } else {
      const group = altGroupToDisplayGroup(
        entry,
        false,
        metrics,
        t,
        applyBenchmarkData,
      );
      if (group) groups.push(group);
    }
  }

  return groups;
}
